'use client';

import { useEffect, useRef, useState } from 'react';

import Classes from './style.module.scss';

import { mediaURL } from '@/lib/sslByDomain';
import { cn } from '@/lib/utils';

import { VideoPlayerPropsInferface } from '@/models/Videojs';

import VideoPoster from './VideoPoster';
import NextImage from '../NextImage';

interface VideoPlayerProps extends VideoPlayerPropsInferface {
  desktop?: boolean;
}

export default function VideoPlayer(props: VideoPlayerProps) {
  const {
    src,
    sectionTitle,
    sectionDescription,
    poster,
    videoposter,
    desktop,
  } = props;
  const videoPosterRef = useRef<HTMLVideoElement | null>(null);
  const [posterSSL, setPosterSSL] = useState<string | undefined>();
  const [videPosterSSL, setVidePosterSSL] = useState<string | undefined>();
  const [loadPlayer, setLoadPlayer] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (poster?.mediaItemUrl) {
      const url = mediaURL(poster.mediaItemUrl);

      setPosterSSL(url);
    }
  }, [poster]);

  useEffect(() => {
    if (videoposter?.mediaItemUrl) {
      const url = mediaURL(videoposter.mediaItemUrl);
      setVidePosterSSL(url);
    }
  }, [videoposter]);

  return (
    <>
      {!loadPlayer ? (
        <div
          className='section-highlights__poster relative h-full w-full cursor-pointer bg-primary-950'
          onClick={() => {
            setLoading(true);
            setLoadPlayer(true);
          }}
        >
          <div
            className={cn(
              'section-highlights__poster--video pointer-events-none absolute left-0 top-0 z-10 flex aspect-video h-full w-full flex-col items-center justify-center gap-y-12 bg-primary-900 text-center',
            )}
          >
            {desktop && sectionTitle && (
              <VideoPoster
                sectionTitle={sectionTitle}
                sectionDescription={sectionDescription}
                poster={poster}
                videoposter={videoposter}
              />
            )}
            <div
              data-aos='fade-in'
              data-aos-delay='300'
              data-aos-duration='600'
              data-aos-anchor='#section-highlights'
              aria-hidden='true'
            >
              <NextImage
                src='/images/icon-play.svg'
                width='120'
                height='120'
                alt='Icon'
                className='mx-auto h-[60px] w-[60px] sm:h-20 sm:w-20 lg:h-24 lg:w-24 xl:h-[120px] xl:w-[120px]'
              />
            </div>
            {videPosterSSL ? (
              <div
                className='section-highlights__video--poster-video absolute left-1/2 top-1/2 -z-10  h-full w-full -translate-x-1/2 -translate-y-1/2 bg-primary-950 bg-opacity-60'
                aria-hidden='true'
              >
                <video
                  loop
                  autoPlay
                  muted
                  className='transform bg-primary-950 object-cover opacity-60 mix-blend-color-burn'
                  poster={posterSSL || ''}
                  ref={videoPosterRef}
                >
                  <source src={videPosterSSL as string} type='video/mp4' />
                </video>
              </div>
            ) : (
              posterSSL && (
                <NextImage
                  useSkeleton
                  src={posterSSL as string}
                  alt={
                    (poster?.altText as string) ||
                    'CERN Knowledge Transfer 2023 Highlights'
                  }
                  className='video-background absolute left-0 top-0 -z-10 h-full w-full'
                  classNames={{ image: 'object-cover w-full h-full' }}
                  fill
                  sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 1403px'
                  aria-hidden='true'
                />
              )
            )}
          </div>
        </div>
      ) : (
        <div className='section-highlights__iframe aspect-video overflow-hidden rounded-2xl bg-primary-950'>
          <iframe
            src={`${src}?responsive=1`}
            className='relative z-10 aspect-video w-full rounded-2xl bg-primary-950'
            allow='fullscreen'
            onLoad={() => {
              setLoading(false);
              setLoadPlayer(true);
            }}
          />
          {loading && (
            <div className='pointer-events-none absolute left-1/2 top-1/2 z-0 -translate-x-1/2 -translate-y-1/2 transform'>
              <svg className={Classes.spinner} viewBox='0 0 50 50'>
                <circle
                  className={Classes.path}
                  cx='25'
                  cy='25'
                  r='20'
                  fill='none'
                  strokeWidth='5'
                ></circle>
              </svg>
            </div>
          )}
        </div>
      )}
    </>
  );
}
